import StarRating from '../base/StarRating';
import { useEpisodeRatings } from '../../hooks/useEpisodeRatings';

export default function RatingsTally() {
  const { allRatings, averageRating } = useEpisodeRatings();

  return (
    <section
      style={{
        background: 'linear-gradient(160deg, #1c0a02 0%, #3b1505 50%, #4c1a07 100%)',
      }}
      className="py-14 lg:py-16 border-t border-amber-800/40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-500/20">
                <i className="ri-bar-chart-2-line text-amber-400 text-sm" />
              </div>
              <span className="font-sans text-xs font-semibold tracking-widest uppercase text-amber-500">
                Episode Ratings
              </span>
            </div>
            <h2 className="font-serif text-2xl lg:text-3xl font-bold text-amber-100">
              Your Running Tally
            </h2>
            <p className="font-sans text-sm text-amber-300/50 mt-1">
              Ratings are saved in your browser. Rate any episode on the{' '}
              <a href="/episodes" className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors">
                Episodes page
              </a>
              .
            </p>
          </div>

          {/* Overall average */}
          {allRatings.length > 0 && (
            <div className="flex flex-col items-start sm:items-end gap-1.5 bg-amber-500/10 border border-amber-500/20 rounded-2xl px-6 py-4 flex-shrink-0">
              <span className="font-sans text-xs text-amber-400/70 uppercase tracking-widest">Your Avg</span>
              <div className="flex items-center gap-3">
                <span className="font-serif font-bold text-amber-400 text-3xl leading-none">
                  {averageRating.toFixed(1)}
                </span>
                <StarRating value={Math.round(averageRating)} readonly size="sm" />
              </div>
              <span className="font-sans text-xs text-amber-300/50">
                across {allRatings.length} episode{allRatings.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Tally list or empty state */}
        {allRatings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-4 border border-amber-800/30 rounded-2xl bg-amber-900/10">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-500/10">
              <i className="ri-star-line text-amber-500/50 text-2xl" />
            </div>
            <div className="text-center">
              <p className="font-sans font-semibold text-amber-200/60 text-sm mb-1">No ratings yet</p>
              <p className="font-sans text-xs text-amber-300/40 max-w-xs">
                Once episodes are released, head to the Episodes page to leave your first rating.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allRatings.map((entry) => (
              <div
                key={entry.episodeId}
                className="flex items-center gap-4 bg-amber-900/20 border border-amber-700/30 rounded-xl px-5 py-4 hover:border-amber-600/50 transition-colors duration-200"
              >
                {/* Ep number badge */}
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/30">
                  <span className="font-serif font-bold text-amber-400 text-sm">{entry.episodeNumber}</span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-xs text-amber-400/60 font-semibold uppercase tracking-wide mb-0.5">
                    Episode {entry.episodeNumber}
                  </p>
                  <p className="font-serif font-bold text-amber-100 text-sm leading-snug truncate mb-2">
                    {entry.episodeTitle}
                  </p>
                  <StarRating value={entry.rating} readonly size="sm" />
                </div>

                {/* Star score */}
                <div className="flex-shrink-0 text-right">
                  <span className="font-serif font-bold text-amber-400 text-xl leading-none">{entry.rating}</span>
                  <span className="font-sans text-xs text-amber-400/50 block">/5</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
